var _excluded = ["disabled", "fromFormat", "name", "onChange", "required", "size", "toFormat", "value", "variant", "yearRange", "selectProps"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from './Autocomplete';
import FormControl from './FormControl';
import Grid from './Grid';
import Text from './Text';
import StyledDateSelect from './DateSelect.styled';
import date from '../utils/helpers/date';
import { dayOptions, monthOptions, yearOptions } from '../utils/helpers/dateSelect';
import { isFunction } from '../utils/helpers';
function DateSelect(_ref) {
  var disabled = _ref.disabled,
    fromFormat = _ref.fromFormat,
    name = _ref.name,
    onChange = _ref.onChange,
    required = _ref.required,
    size = _ref.size,
    toFormat = _ref.toFormat,
    value = _ref.value,
    variant = _ref.variant,
    yearRange = _ref.yearRange,
    selectProps = _ref.selectProps,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var _useState = useState(''),
    day = _useState[0],
    setDay = _useState[1];
  var _useState2 = useState(''),
    month = _useState2[0],
    setMonth = _useState2[1];
  var _useState3 = useState(''),
    year = _useState3[0],
    setYear = _useState3[1];
  useEffect(function () {
    var parsed = date(value, fromFormat);
    if (parsed.isValid()) {
      setDay(parsed.format('DD'));
      setMonth(parsed.format('MM'));
      setYear(parsed.format('YYYY'));
    }
  }, [fromFormat, value]);
  var handleChange = function handleChange(y, m, d) {
    if (y === void 0) {
      y = '';
    }
    if (m === void 0) {
      m = '';
    }
    if (d === void 0) {
      d = '';
    }
    if (y && m && d) {
      var values = y + "-" + m + "-" + d;
      var parsed = date(values, 'YYYY-MM-DD');
      if (isFunction(onChange)) {
        onChange(!parsed.isValid() ? '' : parsed.format(toFormat));
      }
    }
  };
  var ddProps = _extends({
    disabled: disabled,
    noOptionsText: '',
    fullWidth: true,
    disableClearable: true,
    required: required,
    size: size,
    variant: variant,
    freeSolo: true
  }, selectProps);
  var separator = /*#__PURE__*/React.createElement(Grid, {
    item: true,
    sx: {
      color: 'default.light'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    color: "inherit",
    variant: "body1"
  }, "/"));
  return /*#__PURE__*/React.createElement(FormControl, _extends({
    helperTextPlacement: "below",
    fullWidth: true
  }, rest, {
    name: name
  }), /*#__PURE__*/React.createElement(StyledDateSelect, null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    alignItems: "center",
    sx: {
      flexWrap: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    sx: {
      flexGrow: 0.5,
      minWidth: {
        xs: 70,
        sm: 80
      }
    }
  }, /*#__PURE__*/React.createElement(Autocomplete, _extends({}, ddProps, {
    name: name + "_day",
    options: dayOptions(),
    onInputChange: function onInputChange(_, val) {
      setDay(val);
      handleChange(year, month, val);
    },
    placeholder: "DD",
    value: day
  }))), separator, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    sx: {
      flexGrow: 0.5,
      minWidth: {
        xs: 70,
        sm: 80
      }
    }
  }, /*#__PURE__*/React.createElement(Autocomplete, _extends({}, ddProps, {
    name: name + "_month",
    options: monthOptions(),
    onInputChange: function onInputChange(_, val) {
      setMonth(val);
      handleChange(year, val, day);
    },
    placeholder: "MM",
    value: month
  }))), separator, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    sx: {
      flexGrow: 2,
      minWidth: {
        sm: 110
      }
    }
  }, /*#__PURE__*/React.createElement(Autocomplete, _extends({}, ddProps, {
    name: name + "_year",
    options: yearOptions(yearRange),
    onInputChange: function onInputChange(_, val) {
      setYear(val);
      handleChange(val, month, day);
    },
    placeholder: "YYYY",
    value: year
  }))))));
}
DateSelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the input will be not be intractive.
   */
  disabled: PropTypes.bool,
  /**
   * The  format in which `value` is provided.
   *
   * @see https://day.js.org/docs/en/display/format#list-of-all-available-formats
   */
  fromFormat: PropTypes.string,
  /**
   *
   */
  name: PropTypes.string.isRequired,
  /**
   *
   */
  onChange: PropTypes.func.isRequired,
  /**
   *
   */
  required: PropTypes.bool,
  /**
   *
   */
  size: PropTypes.oneOf(['medium', 'small']),
  /**
   * The  format in which new `value` will be returned after selection.
   *
   * @see https://day.js.org/docs/en/display/format#list-of-all-available-formats
   */
  toFormat: PropTypes.string,
  /**
   * The value of the `input` element.
   */
  value: PropTypes.string,
  /**
   *
   */
  variant: PropTypes.oneOf(['filled', 'outlined']),
  /**
   * The value of the `input` element.
   */
  yearRange: PropTypes.string,
  /**
   *
   * @see https://v4.mui.com/api/autocomplete
   */
  selectProps: PropTypes.shape({})
} : {};
DateSelect.defaultProps = {
  disabled: false,
  fromFormat: 'YYYY-MM-DD',
  required: false,
  size: 'medium',
  toFormat: 'YYYY-MM-DD',
  value: '',
  variant: 'outlined',
  yearRange: '-5:5',
  selectProps: {}
};
export default DateSelect;